/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ui/molecules/Modal';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { Loader } from '@ui';
import { Table, TableSize } from '@ui/molecules/Table';
import { listRuleVersions, rollbackRule } from '../services/TkbService';
/**
 * Per-rule version history drawer (task-11 §Step 4): paginated append-only
 * chain, approver/timestamps, and "Restore this version" → rollback endpoint
 * with a confirm step. directive_machine is never shown (only directive_human).
 */
export default function VersionDrawer({ rule, rulesQueryKey, onClose, t }) {
    var _a;
    const qc = useQueryClient();
    const [confirmVersion, setConfirmVersion] = useState(null);
    const [rollbackError, setRollbackError] = useState(null);
    const { data, isLoading, isError } = useQuery({
        queryKey: ['tkb', 'rule_versions', rule.id],
        queryFn: () => listRuleVersions(rule.id)
    });
    const rollbackMutation = useMutation({
        mutationFn: (toVersion) => rollbackRule(rule.id, toVersion),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: rulesQueryKey });
            setConfirmVersion(null);
            onClose();
        },
        onError: (err) => setRollbackError(err.message)
    });
    // Show the latest version first (Current at top) per the design AC. The list
    // endpoint returns the append-only chain oldest-first, so sort descending by
    // version number here rather than depending on server order (IP-14860).
    const versions = [...((_a = data === null || data === void 0 ? void 0 : data.versions) !== null && _a !== void 0 ? _a : [])];
    versions.sort((a, b) => b.version - a.version);
    const columns = [
        {
            dataKey: 'version',
            label: t('versions.headers.version'),
            columnType: 'primary',
            width: '140px',
            // "Current" is shown as an inline pill next to the version number
            // (matches the mockup) rather than a separate Status column.
            renderer: ({ item }) => (React.createElement("span", { className: "tkb-version-cell" },
                "v",
                item.version,
                item.active && React.createElement("span", { className: "tkb-version-cell__badge" }, t('versions.current'))))
        },
        {
            dataKey: 'directive_human',
            label: t('versions.headers.directive')
        },
        {
            dataKey: 'updated_at',
            label: t('versions.headers.updated'),
            width: '20%',
            renderer: ({ item }) => React.createElement(React.Fragment, null, new Date(item.updated_at).toLocaleString())
        },
        {
            dataKey: 'actions',
            label: t('versions.headers.actions'),
            width: '128px',
            align: 'right',
            columnType: 'last',
            renderer: ({ item }) => item.active ? null : (React.createElement(Button, { variant: "secondary", onClick: () => {
                    setRollbackError(null);
                    setConfirmVersion(item.version);
                } }, t('versions.restore')))
        }
    ];
    return (React.createElement(Modal, { isOpen: true, onClose: onClose, ariaLabel: t('versions.title') },
        React.createElement(ModalHeader, { title: t('versions.title') }),
        React.createElement(ModalBody, null,
            isLoading && (React.createElement("div", { className: "loading-center" },
                React.createElement(Loader, { size: "32" }))),
            isError && React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)' } }, t('versions.load_failure')),
            rollbackError && (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)', marginBottom: '8px' } }, rollbackError)),
            !isLoading && !isError && (React.createElement("div", { "data-testid": "version-chain" },
                React.createElement(Table, { variant: "detail", size: TableSize.Full, columns: columns, data: versions })))),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { variant: "secondary", onClick: onClose }, t('versions.close'))),
        confirmVersion !== null && (React.createElement(Modal, { isOpen: true, onClose: () => setConfirmVersion(null), ariaLabel: t('versions.confirm_title') },
            React.createElement(ModalHeader, { title: t('versions.confirm_title') }),
            React.createElement(ModalBody, null,
                React.createElement(Text, null, t('versions.confirm_body', { version: confirmVersion }))),
            React.createElement(ModalFooter, null,
                React.createElement(Button, { variant: "secondary", onClick: () => setConfirmVersion(null), style: { marginRight: '8px' } }, t('versions.confirm_cancel')),
                React.createElement(Button, { variant: "primary", disabled: rollbackMutation.isLoading, onClick: () => rollbackMutation.mutate(confirmVersion) }, rollbackMutation.isLoading ? t('versions.restoring') : t('versions.confirm_restore')))))));
}
